import { Check, Phone, PhoneOff } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { cn } from "@/shared/lib/utils";

import type { MapLeadItem } from "../../api/mapLeadService";
import { leadCountLabel } from "../../model/leadCount";

interface Props {
  items: MapLeadItem[];
  /** v5 无命中总数,翻页语义=本页满员即可能有下一页。 */
  hasMore: boolean;
  page: number;
  loading: boolean;
  searched: boolean;
  selectedPoiId: string | null;
  checked: Set<string>;
  joinedPoiIds: Set<string>;
  onSelect: (poiId: string) => void;
  onToggleChecked: (poiId: string) => void;
  onConvertOne: (item: MapLeadItem) => void;
  onConvertChecked: () => void;
  onGotoPage: (page: number) => void;
}

function scoreColor(score: number): string {
  if (score >= 80) return "text-orange-500";
  if (score >= 60) return "text-primary";
  return "text-muted-foreground";
}

function distanceLabel(m: number | null): string | null {
  if (m == null) return null;
  return m < 1000 ? `${m}m` : `${(m / 1000).toFixed(1)}km`;
}

/** 潜客列表:规则分 + 推荐理由 + 单条/批量加入。无导出(合规四道闸之一,定稿拍板⑧)。 */
export function LeadList({
  items,
  hasMore,
  page,
  loading,
  searched,
  selectedPoiId,
  checked,
  joinedPoiIds,
  onSelect,
  onToggleChecked,
  onConvertOne,
  onConvertChecked,
  onGotoPage,
}: Props) {
  if (!searched) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        设置搜索条件后点「开始搜索」,潜在客户会出现在这里
      </div>
    );
  }
  if (!loading && items.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <p className="text-sm font-medium">该区域暂未找到符合条件的企业</p>
        <p className="mt-1 text-xs text-muted-foreground">
          建议:扩大搜索半径 / 减少筛选条件 / 尝试更通用的关键词
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-3 border-b px-4 py-2.5">
        <span className="text-sm font-semibold" data-testid="map-lead-count">
          {leadCountLabel(page, items.length, hasMore)}
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            · 第 {page} 页 · 本页 <span className="tabular-nums">{items.length}</span> 个
          </span>
        </span>
        <span className="text-xs text-muted-foreground">按评分排序</span>
        {checked.size > 0 && (
          <Button size="sm" className="ml-auto h-7" onClick={onConvertChecked}>
            批量加入线索({checked.size})
          </Button>
        )}
      </div>

      <ul className="divide-y">
        {items.map((item) => {
          const joined = joinedPoiIds.has(item.poiId);
          return (
            <li key={item.poiId}>
              <div
                role="button"
                tabIndex={0}
                className={cn(
                  "flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/40",
                  item.poiId === selectedPoiId && "bg-accent/60"
                )}
                onClick={() => onSelect(item.poiId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(item.poiId);
                  }
                }}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={checked.has(item.poiId)}
                    onCheckedChange={() => onToggleChecked(item.poiId)}
                    aria-label={`选择 ${item.name}`}
                  />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{item.name}</span>
                    {item.industry && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-2xs text-primary">
                        {item.industry}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    {item.address && <span className="truncate">{item.address}</span>}
                    {item.phone ? (
                      <span className="inline-flex shrink-0 items-center gap-1 tabular-nums">
                        <Phone className="h-3 w-3" aria-hidden />
                        {item.phone}
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center gap-1 text-amber-600">
                        <PhoneOff className="h-3 w-3" aria-hidden />
                        无电话
                      </span>
                    )}
                    {distanceLabel(item.distanceMeters) && (
                      <span className="shrink-0 tabular-nums">
                        {distanceLabel(item.distanceMeters)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="hidden w-44 shrink-0 text-2xs leading-relaxed text-muted-foreground md:block">
                  {item.reasons.slice(0, 3).map((r) => (
                    <div key={r} className="truncate">
                      {r === "无公开联系电话" ? "⚠ " : "✓ "}
                      {r}
                    </div>
                  ))}
                </div>

                <div className="w-16 shrink-0 text-right">
                  <span className={cn("text-xl font-bold tabular-nums", scoreColor(item.score))}>
                    {item.score}
                  </span>
                  <span className="text-2xs text-muted-foreground"> /100</span>
                </div>

                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant={joined ? "outline" : "default"}
                    className="h-7 text-xs"
                    disabled={joined}
                    onClick={() => onConvertOne(item)}
                  >
                    {joined ? (
                      <>
                        <Check className="mr-1 h-3 w-3" />
                        已加入
                      </>
                    ) : (
                      "加入线索"
                    )}
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {(hasMore || page > 1) && (
        <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
          <span className="tabular-nums">20 条/页</span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              disabled={page <= 1 || loading}
              onClick={() => onGotoPage(page - 1)}
            >
              上一页
            </Button>
            <span className="px-2 tabular-nums">第 {page} 页</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              disabled={!hasMore || loading}
              onClick={() => onGotoPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
